import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CampaignService } from 'src/app/private/services/campaign.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { Chart, registerables, Plugin } from 'chart.js';
import {
  campaignDonutChartOptions,
  createCampaignDonutLegendPlugin,
} from '../shared/campaign-donut-chart.util';
import { getCampaignEventTypeBadgeClass } from 'src/app/utils/status-badge.util';
import {
  extractWhatsappError,
  ExtractedWhatsappError,
  safeJsonParse,
} from 'src/app/utils/whatsapp-error.util';
import { ToastrService } from 'ngx-toastr';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { PipeLineService } from 'src/app/private/services/pipeline.service';

Chart.register(...registerables);

const barValueLabelPlugin: Plugin = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const isHorizontal = chart.options.indexAxis === 'y';

    ctx.save();
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.fillStyle = '#334155';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        const value = dataset.data[index] as number;
        if (!value) return;

        const { x, y } = element.getProps(['x', 'y'], true);
        if (isHorizontal) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(value), x + 6, y);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(String(value), x, y - 4);
        }
      });
    });

    ctx.restore();
  },
};

Chart.register(barValueLabelPlugin);

// Interfaces
interface TrackingEvent {
  id: string;
  campaign_id: string;
  client_id: string;
  message_id: string;
  recipient_phone: string;
  recipient_name?: string | null;
  extra_fields?: Record<string, unknown> | string | null;
  lead_id?: number | string | null;
  contact_id?: number | string | null;
  template_id: string;
  event_type: string;
  status: string;
  event_count: number;
  event_at: string;
  raw_event_data: string;
  createdAt: string;
  updatedAt: string;
  errorInfo?: ExtractedWhatsappError | null;
}

interface CampaignStatistics {
  total: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
}

interface CampaignAnalytics {
  tracking_data: TrackingEvent[];
  statistics: CampaignStatistics;
}

interface StatCard {
  label: string;
  value: number;
  percentage: number;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}

@Component({
  selector: 'app-whatsapp-campaign-analytics',
  templateUrl: './whatsapp-campaign-analytics.component.html',
  styleUrls: ['./whatsapp-campaign-analytics.component.scss'],
})
export class WhatsappCampaignAnalyticsComponent implements OnInit {
  isLoading = true;
  campaignId: string = '';

  // Data
  analyticsData: CampaignAnalytics | null = null;
  statistics: CampaignStatistics | null = null;
  trackingEvents: TrackingEvent[] = [];
  currentCampaign: any = null;
  campaignName: string = '';
  currentTemplateId: any = null;
  groupName: string = '';
  groupTotalContacts: number = 0;
  campaignDate: string | Date | null = null;

  // Computed Stats
  statCards: StatCard[] = [];

  // Filters
  selectedEventType: string = 'Send';
  eventTypes: string[] = ['Send', 'Delivered', 'Read', 'Failed'];

  // Failed Analysis & Error Details
  selectedFailedCategory: string = 'all';
  showErrorModal = false;
  selectedEventForModal: TrackingEvent | null = null;
  showRawJsonInModal = false;

  // Re-run Campaign Modal State
  showRerunModal = false;
  rerunGroupName = '';
  rerunSubmitting = false;
  rerunErrorMessage = '';

  // Add to Lead Modal State
  showAddLeadModal = false;
  isSavingLead = false;
  addLeadErrorMessage = '';
  selectedEventForLead: TrackingEvent | null = null;
  leadSpaceListForModal: any[] = [];
  leadStatusListForModal: Array<{ value: string; label: string }> = [];
  addLeadForm = {
    name: '',
    phone: '',
    email: '',
    status: 'New',
    leadSpaceId: null as string | number | null,
  };

  // Bulk Selection State
  selectedEvents: TrackingEvent[] = [];

  // Bulk Lead Creation Modal State
  showBulkLeadModal = false;
  isBulkSaving = false;
  bulkLeadErrorMessage = '';
  bulkLeadRows: Array<{ name: string; phone: string }> = [];
  bulkLeadForm = {
    status: 'New',
    leadSpaceId: null as string | number | null,
  };
  bulkProgress = { done: 0, total: 0 };

  failedSummary = {
    total: 0,
    retriable: 0,
    cannotRetry: 0,
    notOnWhatsapp: 0,
    metaRestricted: 0,
    rateLimit: 0,
    templateIssue: 0,
    other: 0,
  };

  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  // Charts
  timelineChartInstance: any = null;
  funnelChartInstance: any = null;
  donutChartInstance: any = null;
  timelineChartSubtitle = '';

  private readonly timelineMetrics = ['Send', 'Delivered', 'Read', 'Failed'];
  private readonly timelineColors: Record<string, string> = {
    Send: '#25D366',
    Delivered: '#10b981',
    Read: '#3b82f6',
    Failed: '#ef4444',
  };
  private readonly timelineLabels: Record<string, string> = {
    Send: 'Sent',
    Delivered: 'Delivered',
    Read: 'Read',
    Failed: 'Failed',
  };

  // View Mode
  viewMode: 'table' | 'graph' = 'table';

  hasOverflowExtraFields = false;
  extraFieldsPopup: {
    name: string;
    phone: string;
    items: { label: string; value: string }[];
  } | null = null;

  constructor(
    private campaignService: CampaignService,
    private manageCampaignService: ManageCampaignService,
    private contactGroupService: ContactGrouopService,
    private leadService: LeadService,
    private pipelineService: PipeLineService,
    private localStorageService: LocalStorageService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.campaignId = this.route.snapshot.params['id'];
    this.fetchCampaignDetails();
    this.getCampaignTracking();
  }

  fetchCampaignDetails() {
    const idVal = Number(this.campaignId) || this.campaignId;
    const clientId = this.localStorageService.getClientId();

    const payload: any = {
      where: { id: idVal },
      page: 0,
      limit: 10,
      order: {
        col: '"campaign"."createdAt"',
        dir: 'DESC',
      },
    };
    if (clientId) {
      payload.where.client_id = clientId;
    }

    this.manageCampaignService.getAllManangeCampaign(payload).subscribe({
      next: (res: any) => {
        const list = res?.data || res?.rows || (Array.isArray(res) ? res : []);
        let found =
          list.find((c: any) => String(c.id) === String(this.campaignId)) ||
          list[0];
        if (found) {
          this.applyCampaignDetails(found);
        } else if (payload.where.client_id) {
          // Retry without client_id in case scope is higher or admin
          const fallbackPayload = { ...payload, where: { id: idVal } };
          this.manageCampaignService
            .getAllManangeCampaign(fallbackPayload)
            .subscribe({
              next: (fallbackRes: any) => {
                const fList =
                  fallbackRes?.data ||
                  fallbackRes?.rows ||
                  (Array.isArray(fallbackRes) ? fallbackRes : []);
                const fFound =
                  fList.find(
                    (c: any) => String(c.id) === String(this.campaignId),
                  ) || fList[0];
                if (fFound) {
                  this.applyCampaignDetails(fFound);
                }
              },
              error: (e) =>
                console.warn('Could not load fallback campaign details:', e),
            });
        }
      },
      error: (err: any) => {
        console.warn('Could not load campaign details:', err);
      },
    });
  }

  applyCampaignDetails(found: any) {
    this.currentCampaign = found;
    this.campaignName =
      found.campaign_name || found.name || `Campaign #${this.campaignId}`;
    this.currentTemplateId = found.template_id;
    this.campaignDate = found.createdAt || found.schedule_date || null;

    if (found.contact_group?.name) {
      this.groupName = found.contact_group.name;
      this.groupTotalContacts = found.contact_group.total_contacts || 0;
    } else if (found.contact_group_id) {
      this.fetchGroupName(found.contact_group_id);
    }
  }

  fetchGroupName(groupId: any) {
    this.contactGroupService
      .getAllContactGroups({
        where: { id: groupId },
      })
      .subscribe({
        next: (res: any) => {
          const groups =
            res?.data || res?.rows || (Array.isArray(res) ? res : []);
          const g =
            groups.find((item: any) => String(item.id) === String(groupId)) ||
            groups[0];
          if (g) {
            this.groupName = g.name || this.groupName;
            this.groupTotalContacts =
              g.total_contacts || this.groupTotalContacts;
          }
        },
        error: (err: any) =>
          console.warn('Could not fetch contact group:', err),
      });
  }

  formatFullDate(dateVal: any): string {
    if (!dateVal) return '—';
    const date = new Date(dateVal);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getCampaignStatusBadgeClass(status: string): string {
    const s = String(status || '').toLowerCase();
    if (s.includes('complete') || s.includes('success'))
      return 'badge-status-completed';
    if (s.includes('progress') || s.includes('run'))
      return 'badge-status-running';
    if (s.includes('pending') || s.includes('schedule'))
      return 'badge-status-pending';
    if (s.includes('fail') || s.includes('error')) return 'badge-status-failed';
    return 'badge-status-default';
  }

  openLeadProfile(leadId: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!leadId) {
      this.toastr.warning('No Lead ID linked to this recipient');
      return;
    }

    const hashPath = this.router.serializeUrl(
      this.router.createUrlTree(['/main/leads', leadId]),
    );
    const url = `${window.location.origin}${window.location.pathname}#${hashPath}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openContactProfile(contactId: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!contactId) {
      this.toastr.warning('No Contact ID linked to this recipient');
      return;
    }

    const hashPath = this.router.serializeUrl(
      this.router.createUrlTree(['/main/contacts', contactId]),
    );
    const url = `${window.location.origin}${window.location.pathname}#${hashPath}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getCampaignTracking() {
    this.isLoading = true;
    const data = {
      client_id: this.localStorageService.getClientId(),
      campaign_id: this.campaignId,
    };

    this.campaignService.getWhatsappCampaignTracking(data).subscribe({
      next: (res: any) => {
        console.log('WhatsApp Campaign Analytics:', res);
        this.analyticsData = res;
        this.statistics = res?.statistics || {
          total: 0,
          sent: 0,
          delivered: 0,
          read: 0,
          failed: 0,
        };
        this.trackingEvents = (res?.tracking_data || []).map((event: any) => ({
          ...event,
          errorInfo: extractWhatsappError(event.raw_event_data),
        }));
        this.refreshExtraFieldColumns();
        this.calculateFailedSummary();
        this.calculateStatCards();
        this.updatePagination();
        this.isLoading = false;

        // Initialize Charts
        setTimeout(() => {
          this.initAllCharts();
        }, 100);
      },
      error: (err) => {
        console.error('Error fetching WhatsApp campaign analytics:', err);
        this.statistics = {
          total: 0,
          sent: 0,
          delivered: 0,
          read: 0,
          failed: 0,
        };
        this.trackingEvents = [];
        this.refreshExtraFieldColumns();
        this.calculateFailedSummary();
        this.calculateStatCards();
        this.isLoading = false;
      },
    });
  }

  get deliveryRate(): number {
    const sent = this.statistics?.sent || 0;
    const delivered = this.statistics?.delivered || 0;
    if (sent <= 0) return 0;
    const rate = (delivered / sent) * 100;
    return isNaN(rate) || !isFinite(rate)
      ? 0
      : Math.min(100, Math.max(0, rate));
  }

  get readRate(): number {
    const delivered = this.statistics?.delivered || 0;
    const read = this.statistics?.read || 0;
    if (delivered <= 0) return 0;
    const rate = (read / delivered) * 100;
    return isNaN(rate) || !isFinite(rate)
      ? 0
      : Math.min(100, Math.max(0, rate));
  }

  get failureRate(): number {
    const sent = this.statistics?.sent || 0;
    const failed = this.statistics?.failed || 0;
    if (sent <= 0) return 0;
    const rate = (failed / sent) * 100;
    return isNaN(rate) || !isFinite(rate)
      ? 0
      : Math.min(100, Math.max(0, rate));
  }

  calculateStatCards() {
    if (!this.statistics) return;

    const safePct = (numerator: number, divisor: number): number => {
      if (!divisor || divisor <= 0 || !numerator || numerator <= 0) return 0;
      const pct = (numerator / divisor) * 100;
      return isNaN(pct) || !isFinite(pct) ? 0 : Math.min(100, Math.max(0, pct));
    };

    const total = this.statistics.total || 0;
    const sent = this.statistics.sent || 0;
    const delivered = this.statistics.delivered || 0;
    const read = this.statistics.read || 0;
    const failed = this.statistics.failed || 0;

    const baseForDelivery = sent > 0 ? sent : total;

    this.statCards = [
      {
        label: 'Total Sent',
        value: sent,
        percentage: safePct(sent, total > 0 ? total : sent),
        icon: 'bi-send-fill',
        color: '#25D366', // WhatsApp green
        trend: 'neutral',
      },
      {
        label: 'Delivered',
        value: delivered,
        percentage: safePct(delivered, baseForDelivery),
        icon: 'bi-check-circle-fill',
        color: '#10b981',
        trend: delivered > 0 ? 'up' : 'neutral',
      },
      {
        label: 'Read',
        value: read,
        percentage: safePct(read, delivered > 0 ? delivered : baseForDelivery),
        icon: 'bi-check-all',
        color: '#3b82f6',
        trend: read > 0 ? 'up' : 'neutral',
      },
      {
        label: 'Failed',
        value: failed,
        percentage: safePct(failed, baseForDelivery),
        icon: 'bi-exclamation-triangle-fill',
        color: '#ef4444',
        trend: failed > 0 ? 'down' : 'neutral',
      },
    ];
  }

  initAllCharts() {
    if (!this.statistics) return;
    this.prepareTimelineData();
    this.initFunnelChart();
    this.initDonutChart();
  }

  initFunnelChart() {
    if (this.funnelChartInstance) this.funnelChartInstance.destroy();

    const canvas = document.getElementById('funnelChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const funnelValues = [
      this.statistics?.sent || 0,
      this.statistics?.delivered || 0,
      this.statistics?.read || 0,
      this.statistics?.failed || 0,
    ];
    const funnelColors = ['#25D366', '#10b981', '#3b82f6', '#ef4444'];

    this.funnelChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Sent', 'Delivered', 'Read', 'Failed'],
        datasets: [
          {
            label: 'Events',
            data: funnelValues,
            backgroundColor: funnelColors.map((color) => `${color}CC`),
            borderColor: funnelColors,
            borderWidth: 1,
            borderRadius: 6,
            barPercentage: 0.55,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 28 } },
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            titleColor: '#1e293b',
            bodyColor: '#475569',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 10,
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.x} messages`,
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: '#f1f5f9' },
            ticks: { stepSize: 1, precision: 0, color: '#94a3b8' },
            border: { display: false },
          },
          y: {
            grid: { display: false },
            ticks: { color: '#475569', font: { size: 12, weight: 600 } },
            border: { display: false },
          },
        },
      },
    });
  }

  private getWhatsappDeliveryStatusData(): {
    labels: string[];
    values: number[];
    colors: string[];
  } {
    const delivered = this.statistics?.delivered || 0;
    const failed = this.statistics?.failed || 0;

    return {
      labels: ['Delivered', 'Failed'],
      values: [delivered, failed],
      colors: ['#10b981', '#ef4444'],
    };
  }

  initDonutChart() {
    if (this.donutChartInstance) this.donutChartInstance.destroy();

    const legendHost = document.getElementById('donutChartLegend');
    if (legendHost) legendHost.innerHTML = '';

    const canvas = document.getElementById('donutChart') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { labels, values, colors } = this.getWhatsappDeliveryStatusData();

    this.donutChartInstance = new Chart(ctx, {
      type: 'doughnut',
      plugins: [
        createCampaignDonutLegendPlugin('donutChartLegend', 'donutChart'),
      ],
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        ...campaignDonutChartOptions,
        plugins: {
          ...campaignDonutChartOptions.plugins,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const value = ctx.parsed || 0;
                const total = values.reduce((sum, n) => sum + n, 0);
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return ` ${ctx.label}: ${value} (${pct}%)`;
              },
            },
          },
        },
      },
    });
  }

  private buildTimelineBuckets(events: TrackingEvent[]): {
    labels: string[];
    getBucketKey: (timestamp: string) => string;
  } {
    if (!events.length) {
      return { labels: [], getBucketKey: () => '' };
    }

    const sorted = [...events].sort(
      (a, b) => new Date(a.event_at).getTime() - new Date(b.event_at).getTime(),
    );
    const start = new Date(sorted[0].event_at);
    const end = new Date(sorted[sorted.length - 1].event_at);
    const sameDay = start.toDateString() === end.toDateString();

    if (sameDay) {
      const labels: string[] = [];
      const cursor = new Date(start);
      cursor.setMinutes(0, 0, 0);
      const endHour = new Date(end);
      endHour.setMinutes(59, 59, 999);

      while (cursor <= endHour) {
        labels.push(
          cursor.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }),
        );
        cursor.setHours(cursor.getHours() + 1);
      }

      const getBucketKey = (timestamp: string) => {
        const date = new Date(timestamp);
        date.setMinutes(0, 0, 0);
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
        });
      };

      return { labels, getBucketKey };
    }

    const labels: string[] = [];
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);

    while (cursor <= endDay) {
      labels.push(
        cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      );
      cursor.setDate(cursor.getDate() + 1);
    }

    const getBucketKey = (timestamp: string) =>
      new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });

    return { labels, getBucketKey };
  }

  prepareTimelineData() {
    if (!this.trackingEvents.length) return;

    if (this.timelineChartInstance) {
      this.timelineChartInstance.destroy();
    }

    const filtered = this.filteredEvents;
    const sourceEvents = filtered.length ? filtered : this.trackingEvents;
    const isAllEvents = this.selectedEventType === 'all';
    const { labels: timeLabels, getBucketKey } =
      this.buildTimelineBuckets(sourceEvents);

    if (isAllEvents && timeLabels.length <= 2) {
      const summaryLabels = this.timelineMetrics.map(
        (metric) => this.timelineLabels[metric],
      );
      const summaryData = this.timelineMetrics.map(
        (metric) =>
          sourceEvents.filter(
            (e) => e.event_type.toLowerCase() === metric.toLowerCase(),
          ).length,
      );

      this.timelineChartSubtitle =
        'Grouped by event status — clearer view for campaigns with few events';
      this.initTimelineChart(
        summaryLabels,
        [
          {
            label: 'Events',
            data: summaryData,
            backgroundColor: this.timelineMetrics.map(
              (metric) => `${this.timelineColors[metric]}CC`,
            ),
            borderColor: this.timelineMetrics.map(
              (metric) => this.timelineColors[metric],
            ),
            borderWidth: 1,
            borderRadius: 8,
            barPercentage: 0.62,
          },
        ],
        false,
        'Event status',
      );
      return;
    }

    this.timelineChartSubtitle =
      timeLabels.length === 1
        ? 'All events occurred in the same time period'
        : '';

    if (isAllEvents) {
      const datasets = this.timelineMetrics.map((metric) => ({
        label: this.timelineLabels[metric],
        data: timeLabels.map(
          (label) =>
            sourceEvents.filter(
              (e) =>
                getBucketKey(e.event_at) === label &&
                e.event_type.toLowerCase() === metric.toLowerCase(),
            ).length,
        ),
        backgroundColor: `${this.timelineColors[metric]}CC`,
        borderColor: this.timelineColors[metric],
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.85,
        categoryPercentage: 0.72,
      }));

      this.initTimelineChart(timeLabels, datasets, false, 'Time period');
      return;
    }

    const barColor =
      this.timelineColors[this.selectedEventType] ||
      this.timelineColors['Send'];

    this.initTimelineChart(
      timeLabels,
      [
        {
          label:
            this.timelineLabels[this.selectedEventType] ||
            this.selectedEventType,
          data: timeLabels.map(
            (label) =>
              sourceEvents.filter((e) => getBucketKey(e.event_at) === label)
                .length,
          ),
          backgroundColor: `${barColor}CC`,
          borderColor: barColor,
          borderWidth: 1,
          borderRadius: 6,
          barPercentage: 0.55,
        },
      ],
      false,
      'Time period',
    );
  }

  initTimelineChart(
    labels: string[],
    datasets: any[],
    stacked = false,
    xAxisTitle = 'Time period',
  ) {
    const canvas = document.getElementById(
      'timelineChart',
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.timelineChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 12 } },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: datasets.length > 1,
            position: 'top',
            align: 'start',
            labels: {
              usePointStyle: true,
              pointStyle: 'rectRounded',
              boxWidth: 10,
              padding: 18,
              font: { size: 12, weight: 500 },
            },
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            titleColor: '#1e293b',
            bodyColor: '#475569',
            borderColor: '#e2e8f0',
            borderWidth: 1,
            padding: 12,
            boxPadding: 4,
            usePointStyle: true,
            titleFont: { size: 13, weight: 'bold' },
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            stacked,
            suggestedMax:
              Math.max(...datasets.flatMap((d) => d.data as number[]), 1) + 1,
            grid: { color: '#f1f5f9' },
            ticks: { stepSize: 1, precision: 0, color: '#94a3b8' },
            border: { display: false },
            title: {
              display: true,
              text: 'Event count',
              color: '#64748b',
              font: { size: 12, weight: 500 },
            },
          },
          x: {
            stacked,
            grid: { display: false },
            ticks: {
              color: '#475569',
              maxRotation: 45,
              minRotation: 0,
              font: { size: 11, weight: 500 },
            },
            border: { display: false },
            title: {
              display: true,
              text: xAxisTitle,
              color: '#64748b',
              font: { size: 12, weight: 500 },
            },
          },
        },
      },
    });
  }

  setViewMode(mode: 'table' | 'graph') {
    this.viewMode = mode;
    if (mode === 'graph') {
      setTimeout(() => {
        this.initAllCharts();
      }, 0);
    }
  }

  // Filter events by type and failure category
  get filteredEvents(): TrackingEvent[] {
    let events = this.trackingEvents;
    if (this.selectedEventType !== 'all') {
      events = events.filter(
        (event) =>
          event.event_type.toLowerCase() ===
          this.selectedEventType.toLowerCase(),
      );
    }

    if (
      this.selectedEventType.toLowerCase() === 'failed' &&
      this.selectedFailedCategory !== 'all'
    ) {
      events = events.filter((event) => {
        const cat = event.errorInfo?.category || 'other';
        return cat === this.selectedFailedCategory;
      });
    }

    return events;
  }

  // Paginated events
  get paginatedEvents(): TrackingEvent[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredEvents.slice(startIndex, endIndex);
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredEvents.length / this.itemsPerPage);
  }

  setEventFilter(eventType: string) {
    this.selectedEventType = eventType;
    this.selectedFailedCategory = 'all';
    this.currentPage = 1;
    this.updatePagination();
    this.prepareTimelineData();
  }

  setFailedCategory(category: string) {
    this.selectedFailedCategory = category;
    this.currentPage = 1;
    this.updatePagination();
  }

  calculateFailedSummary() {
    const failedEvents = this.trackingEvents.filter(
      (e) => e.event_type?.toLowerCase() === 'failed',
    );

    let retriable = 0;
    let cannotRetry = 0;
    let notOnWhatsapp = 0;
    let metaRestricted = 0;
    let rateLimit = 0;
    let templateIssue = 0;
    let other = 0;

    for (const ev of failedEvents) {
      const err = ev.errorInfo;
      if (err) {
        if (err.retriable) {
          retriable++;
        } else {
          cannotRetry++;
        }

        switch (err.category) {
          case 'not_on_whatsapp':
            notOnWhatsapp++;
            break;
          case 'meta_restricted':
            metaRestricted++;
            break;
          case 'rate_limit':
            rateLimit++;
            break;
          case 'template_issue':
            templateIssue++;
            break;
          default:
            other++;
            break;
        }
      } else {
        cannotRetry++;
        other++;
      }
    }

    this.failedSummary = {
      total: failedEvents.length,
      retriable,
      cannotRetry,
      notOnWhatsapp,
      metaRestricted,
      rateLimit,
      templateIssue,
      other,
    };
  }

  openErrorModal(event: TrackingEvent, mouseEv?: MouseEvent) {
    if (mouseEv) {
      mouseEv.stopPropagation();
    }
    this.selectedEventForModal = event;
    this.showErrorModal = true;
    this.showRawJsonInModal = false;
  }

  closeErrorModal() {
    this.showErrorModal = false;
    this.selectedEventForModal = null;
    this.showRawJsonInModal = false;
  }

  toggleRawJsonInModal() {
    this.showRawJsonInModal = !this.showRawJsonInModal;
  }

  copyErrorDetails() {
    if (!this.selectedEventForModal?.errorInfo) return;
    const err = this.selectedEventForModal.errorInfo;
    const ev = this.selectedEventForModal;
    const text = `WhatsApp Delivery Error\nRecipient: ${ev.recipient_name || '—'} (${ev.recipient_phone})\nError Code: ${err.code || 'N/A'}\nTitle: ${err.title}\nDetails: ${err.details}\nMessage: ${err.message}\nResolution: ${err.action}`;
    navigator.clipboard.writeText(text);
  }

  getFormattedRawJson(event: TrackingEvent | null): string {
    if (!event?.raw_event_data) return '{}';
    const parsed = safeJsonParse(event.raw_event_data);
    return JSON.stringify(parsed || event.raw_event_data, null, 2);
  }

  // ============================================
  // Meta Restricted Re-run Campaign Logic
  // ============================================
  get metaRestrictedEvents(): TrackingEvent[] {
    return this.trackingEvents.filter(
      (e) =>
        e.event_type?.toLowerCase() === 'failed' &&
        e.errorInfo?.category === 'meta_restricted',
    );
  }

  get metaRestrictedUniqueRecipients(): {
    phone: string;
    name: string;
    eventAt: Date;
  }[] {
    const map = new Map<
      string,
      { phone: string; name: string; eventAt: Date }
    >();
    for (const ev of this.metaRestrictedEvents) {
      if (ev.recipient_phone && !map.has(ev.recipient_phone)) {
        map.set(ev.recipient_phone, {
          phone: ev.recipient_phone,
          name: ev.recipient_name || ev.recipient_phone,
          eventAt: new Date(ev.event_at || ev.createdAt),
        });
      }
    }
    return Array.from(map.values());
  }

  get metaRestricted24HourStatus() {
    const recipients = this.metaRestrictedUniqueRecipients;
    const totalCount = recipients.length;
    if (!totalCount) {
      return {
        allPassed: true,
        passedCount: 0,
        totalCount: 0,
        minHoursElapsed: 24,
        hoursRemaining: 0,
        cooldownEndsAt: null,
        canRetryNow: true,
      };
    }

    const now = Date.now();
    let passedCount = 0;
    let minHoursElapsed = 999999;
    let newestFailureTime = 0;

    for (const r of recipients) {
      const time = r.eventAt.getTime();
      if (!isNaN(time)) {
        if (time > newestFailureTime) newestFailureTime = time;
        const hours = (now - time) / (1000 * 60 * 60);
        if (hours < minHoursElapsed) minHoursElapsed = hours;
        if (hours >= 24) {
          passedCount++;
        }
      } else {
        passedCount++;
      }
    }

    if (minHoursElapsed === 999999) minHoursElapsed = 24;

    const allPassed = passedCount === totalCount;
    const hoursRemaining = Math.max(0, 24 - minHoursElapsed);
    const cooldownEndsAt = newestFailureTime
      ? new Date(newestFailureTime + 24 * 60 * 60 * 1000)
      : null;

    return {
      allPassed,
      passedCount,
      totalCount,
      minHoursElapsed: Number(minHoursElapsed.toFixed(1)),
      hoursRemaining: Number(hoursRemaining.toFixed(1)),
      cooldownEndsAt,
      canRetryNow: allPassed,
    };
  }

  openRerunModal(mouseEv?: MouseEvent) {
    if (mouseEv) {
      mouseEv.stopPropagation();
    }
    const defaultDate = new Date().toLocaleDateString('en-US');
    const baseName = this.campaignName || `Campaign #${this.campaignId}`;
    this.rerunGroupName = `${baseName} - Rerun (Meta Restricted) - ${defaultDate}`;
    this.showRerunModal = true;
    this.rerunSubmitting = false;
    this.rerunErrorMessage = '';
  }

  closeRerunModal() {
    this.showRerunModal = false;
    this.rerunSubmitting = false;
    this.rerunErrorMessage = '';
  }

  createRerunCampaign() {
    if (!this.rerunGroupName || !this.rerunGroupName.trim()) {
      this.rerunErrorMessage = 'Please enter a valid contact group name.';
      return;
    }

    const recipients = this.metaRestrictedUniqueRecipients;
    if (!recipients.length) {
      this.rerunErrorMessage =
        'No Meta-restricted recipients found to re-target.';
      return;
    }

    const clientId = this.localStorageService.getClientId();
    const userId = this.localStorageService.getUserId();
    const templateId =
      this.currentTemplateId ||
      this.metaRestrictedEvents[0]?.template_id ||
      this.trackingEvents[0]?.template_id;

    if (!templateId) {
      this.rerunErrorMessage =
        'Unable to determine the WhatsApp message template for re-running the campaign.';
      return;
    }

    this.rerunSubmitting = true;
    this.rerunErrorMessage = '';

    const groupPayload: any = {
      name: this.rerunGroupName.trim(),
      client_id: clientId,
      group_type: 'whatsapp',
      total_contacts: recipients.length,
      status: 'Active',
    };

    // 1. Create separate contact group for restricted numbers
    this.contactGroupService.createContact_group(groupPayload).subscribe({
      next: (groupRes: any) => {
        const groupId = groupRes?.id || groupRes?.data?.id;
        if (!groupId) {
          this.rerunErrorMessage =
            'Failed to create new contact group. Please try again.';
          this.rerunSubmitting = false;
          return;
        }

        // 2. Add restricted contacts to this group in background
        const contacts = recipients.map((r) => ({
          phone: r.phone,
          name: r.name || r.phone,
          source: 'meta_restricted_rerun',
        }));

        this.contactGroupService
          .createContactGroupList({
            contact_group_id: groupId,
            client_id: clientId,
            contacts,
          })
          .subscribe({
            next: () => {
              // 3. Create new Broadcast Campaign with Pending status and Same Template
              const campaignPayload: any = {
                campaign_name: this.rerunGroupName.trim(),
                campaign_type: 'whatsapp',
                template_id: templateId,
                client_id: clientId,
                contact_group_id: groupId,
                status: 'Pending', // Pending status as requested
                is_schedule: false,
                created_by: userId,
              };

              if (userId) {
                campaignPayload['user_id'] = userId;
              }

              this.manageCampaignService
                .createCampaign(campaignPayload)
                .subscribe({
                  next: () => {
                    this.rerunSubmitting = false;
                    this.closeRerunModal();
                    this.toastr.success(
                      `Re-run campaign "${this.rerunGroupName.trim()}" created successfully with Pending status!`,
                      'Success',
                    );
                    // Redirect to Broadcast Campaigns
                    setTimeout(() => {
                      this.router.navigate(['/main/broadcast-campaigns']);
                    }, 1000);
                  },
                  error: (campErr: any) => {
                    console.error('Error creating re-run campaign:', campErr);
                    this.rerunErrorMessage =
                      campErr?.error?.message ||
                      'Contact group created, but failed to create campaign.';
                    this.rerunSubmitting = false;
                  },
                });
            },
            error: (contactErr: any) => {
              console.error('Error adding contacts to group:', contactErr);
              this.rerunErrorMessage =
                contactErr?.error?.message ||
                'Failed to add restricted recipients to contact group.';
              this.rerunSubmitting = false;
            },
          });
      },
      error: (groupErr: any) => {
        console.error('Error creating contact group:', groupErr);
        this.rerunErrorMessage =
          groupErr?.error?.message || 'Failed to create contact group.';
        this.rerunSubmitting = false;
      },
    });
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  goToPage(page: number) {
    this.currentPage = page;
  }

  getEventTypeBadgeClass(eventType: string): string {
    return getCampaignEventTypeBadgeClass(eventType);
  }

  getStatusBadgeClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      sent: 'badge-info',
      delivered: 'badge-success',
      read: 'badge-primary',
      failed: 'badge-danger',
    };
    return statusMap[status.toLowerCase()] || 'badge-default';
  }

  getEventIcon(eventType: string): string {
    const iconMap: { [key: string]: string } = {
      send: 'bi-send-fill',
      delivered: 'bi-check-circle-fill',
      read: 'bi-check-all',
      failed: 'bi-exclamation-triangle-fill',
    };
    return iconMap[eventType.toLowerCase()] || 'bi-info-circle-fill';
  }

  private refreshExtraFieldColumns() {
    const seen = new Set<string>();
    const keys: string[] = [];

    this.trackingEvents.forEach((event) => {
      Object.keys(this.parseExtraFields(event.extra_fields)).forEach((key) => {
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        keys.push(key);
      });
    });

    this.hasOverflowExtraFields = keys.length > 0;
  }

  parseExtraFields(value: TrackingEvent['extra_fields']): Record<string, string> {
    if (value == null || value === '') {
      return {};
    }

    let parsed: unknown = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return {};
      }
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const result: Record<string, string> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([key, fieldValue]) => {
      if (fieldValue == null || fieldValue === '') {
        return;
      }
      result[key] = typeof fieldValue === 'object'
        ? JSON.stringify(fieldValue)
        : String(fieldValue);
    });
    return result;
  }

  formatFieldLabel(key: string): string {
    return key
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  extraFieldValue(event: TrackingEvent, key: string): string {
    return this.parseExtraFields(event.extra_fields)[key] || '—';
  }

  hasExtraFields(event: TrackingEvent): boolean {
    return Object.keys(this.parseExtraFields(event.extra_fields)).length > 0;
  }

  openExtraFields(event: TrackingEvent) {
    const extra = this.parseExtraFields(event.extra_fields);
    this.extraFieldsPopup = {
      name: event.recipient_name || 'Contact',
      phone: this.formatPhoneNumber(event.recipient_phone),
      items: Object.keys(extra).map((key) => ({
        label: this.formatFieldLabel(key),
        value: extra[key],
      })),
    };
  }

  closeExtraFields() {
    this.extraFieldsPopup = null;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null || isNaN(value) || !isFinite(value)) {
      return '0.0';
    }
    return Math.min(100, Math.max(0, value)).toFixed(1);
  }

  formatPhoneNumber(phone: string): string {
    if (!phone) {
      return '';
    }
    if (phone.length >= 10) {
      return `+${phone.substring(0, 2)} ${phone.substring(2, 7)} ${phone.substring(7)}`;
    }
    return phone;
  }

  viewRawData(event: TrackingEvent) {
    try {
      const rawData = JSON.parse(event.raw_event_data);
      console.log('Raw Event Data:', rawData);
      alert(JSON.stringify(rawData, null, 2));
    } catch (error) {
      console.error('Error parsing raw event data:', error);
    }
  }

  exportData() {
    const dataStr = JSON.stringify(this.analyticsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp-campaign-${this.campaignId}-analytics.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  goBack() {
    this.router.navigate(['/main/broadcast-campaigns']);
  }

  refreshData() {
    this.getCampaignTracking();
  }

  // openLeadProfile(leadId: any, event?: MouseEvent): void {
  //   if (event) {
  //     event.stopPropagation();
  //     event.preventDefault();
  //   }
  //   if (!leadId) return;
  //   const url = `#/main/leads/${leadId}`;
  //   window.open(url, '_blank');
  // }

  openAddLeadModal(event: TrackingEvent, mouseEv?: MouseEvent): void {
    if (mouseEv) {
      mouseEv.stopPropagation();
      mouseEv.preventDefault();
    }
    this.selectedEventForLead = event;
    this.addLeadErrorMessage = '';
    this.isSavingLead = false;
    this.addLeadForm = {
      name: event.recipient_name || '',
      phone: event.recipient_phone || '',
      email: '',
      status: 'New',
      leadSpaceId: null,
    };
    // Fetch lead spaces for the dropdown
    this.leadSpaceListForModal = [];
    const clientId = this.localStorageService.getClientId();
    this.leadService.getAllLeadSpace({ client_id: clientId }).subscribe({
      next: (res: any) => {
        this.leadSpaceListForModal = Array.isArray(res) ? res : (res?.data || res?.rows || []);
      },
      error: () => { this.leadSpaceListForModal = []; }
    });

    // Fetch pipeline stages for Lead Status
    this.leadStatusListForModal = [];
    this.pipelineService.getAllPipeLine({ client_id: clientId, pipeline_type: 'lead' }).subscribe({
      next: (pipelines: any[]) => {
        const list = Array.isArray(pipelines) ? pipelines : [];
        if (list.length > 0) {
          const firstPipeline = list[0];
          const stages: any[] = firstPipeline.stages || [];
          this.leadStatusListForModal = stages.map((s: any) => ({
            value: s.stage_name,
            label: s.stage_name,
          }));
          // Set default to first stage if current status not found
          const hasCurrentStatus = this.leadStatusListForModal.some(
            (s) => s.value === this.addLeadForm.status
          );
          if (!hasCurrentStatus && this.leadStatusListForModal.length > 0) {
            this.addLeadForm.status = this.leadStatusListForModal[0].value;
          }
        }
      },
      error: () => { this.leadStatusListForModal = []; }
    });

    this.showAddLeadModal = true;
  }

  closeAddLeadModal(): void {
    if (this.isSavingLead) return;
    this.showAddLeadModal = false;
    this.selectedEventForLead = null;
    this.addLeadErrorMessage = '';
    this.addLeadForm.leadSpaceId = null;
    this.leadSpaceListForModal = [];
    this.leadStatusListForModal = [];
  }

  submitAddLead(): void {
    if (!this.addLeadForm.phone || !this.addLeadForm.phone.trim()) {
      this.addLeadErrorMessage = 'Phone number is required.';
      return;
    }

    const clientId = this.localStorageService.getClientId();
    if (!clientId) {
      this.addLeadErrorMessage = 'Client ID is missing. Please re-login.';
      return;
    }

    this.isSavingLead = true;
    this.addLeadErrorMessage = '';

    const cleanPhone = this.addLeadForm.phone.trim();
    const contactName = this.addLeadForm.name
      ? this.addLeadForm.name.trim()
      : `Contact ${cleanPhone}`;

    const now = new Date();
    const lead_date = now.toISOString().split('T')[0];
    const lead_time = now.toTimeString().slice(0, 5);
    const userId = this.localStorageService.getUserId();

    const payload: any = {
      client_id: clientId,
      // Top-level name/phone/email that the createLeads Lambda reads
      lead_name: contactName,
      lead_phone: cleanPhone,
      lead_email: this.addLeadForm.email ? this.addLeadForm.email.trim() : null,
      // Standard lead meta fields
      lead_source: this.campaignName
        ? `WhatsApp Campaign - ${this.campaignName}`
        : 'WhatsApp Campaign',
      lead_status: this.addLeadForm.status || 'New',
      lead_type: 'New Lead',
      lead_date,
      lead_time,
      isEdit: false,
      // Dynamic fields object (phone & name as custom fields for display)
      fields: {
        phone: cleanPhone,
        name: contactName,
        ...(this.addLeadForm.email ? { email: this.addLeadForm.email.trim() } : {}),
      },
    };

    if (userId) {
      payload.created_by = userId;
      payload.user_id = userId;
    }

    // Assign to lead space if selected
    if (this.addLeadForm.leadSpaceId) {
      payload.lead_space_id = this.addLeadForm.leadSpaceId;
    }

    const centerId = this.localStorageService.getCenterId();
    if (centerId) {
      payload.center_id = centerId;
    }

    this.leadService.addLeadData(payload).subscribe({
      next: (res: any) => {
        this.isSavingLead = false;
        const newLeadId =
          res?.lead_id ||
          res?.id ||
          res?.data?.lead_id ||
          res?.data?.id ||
          (typeof res === 'object' && res ? Object.values(res)[0] : null);

        if (this.selectedEventForLead) {
          this.selectedEventForLead.lead_id = newLeadId;
          this.selectedEventForLead.recipient_name = contactName;
        }

        const targetPhone =
          this.selectedEventForLead?.recipient_phone || cleanPhone;
        this.trackingEvents.forEach((evt) => {
          if (evt.recipient_phone === targetPhone) {
            evt.lead_id = newLeadId;
            evt.recipient_name = contactName;
          }
        });

        this.toastr.success(
          newLeadId
            ? `Lead #${newLeadId} created successfully!`
            : 'Lead created successfully!',
          'Success',
        );
        this.closeAddLeadModal();
      },
      error: (err: any) => {
        this.isSavingLead = false;
        console.error('Failed to create lead:', err);
        const errMsg =
          err?.error?.message ||
          err?.message ||
          'Failed to create lead. Please try again.';
        this.addLeadErrorMessage = errMsg;
        this.toastr.error(errMsg, 'Error');
      },
    });
  }

  // =============================================
  // Bulk Selection Helpers
  // =============================================

  /** Selectble = events without a lead_id */
  private selectableEvents(): TrackingEvent[] {
    return this.paginatedEvents.filter((e) => !e.lead_id);
  }

  isEventSelected(event: TrackingEvent): boolean {
    return this.selectedEvents.some((e) => e.id === event.id);
  }

  isAllSelected(): boolean {
    const selectable = this.selectableEvents();
    return selectable.length > 0 && selectable.every((e) => this.isEventSelected(e));
  }

  isSomeSelected(): boolean {
    const selectable = this.selectableEvents();
    return selectable.some((e) => this.isEventSelected(e)) && !this.isAllSelected();
  }

  toggleSelectAll(evt: Event): void {
    const checked = (evt.target as HTMLInputElement).checked;
    if (checked) {
      const toAdd = this.selectableEvents().filter((e) => !this.isEventSelected(e));
      this.selectedEvents = [...this.selectedEvents, ...toAdd];
    } else {
      const pageIds = new Set(this.paginatedEvents.map((e) => e.id));
      this.selectedEvents = this.selectedEvents.filter((e) => !pageIds.has(e.id));
    }
  }

  toggleSelectEvent(event: TrackingEvent, evt: Event): void {
    const checked = (evt.target as HTMLInputElement).checked;
    if (checked) {
      if (!this.isEventSelected(event)) {
        this.selectedEvents = [...this.selectedEvents, event];
      }
    } else {
      this.selectedEvents = this.selectedEvents.filter((e) => e.id !== event.id);
    }
  }

  clearSelection(): void {
    this.selectedEvents = [];
  }

  // =============================================
  // Bulk Lead Creation Modal
  // =============================================

  openBulkLeadModal(): void {
    if (this.selectedEvents.length === 0) return;

    // Pre-fill row names from recipient_name
    this.bulkLeadRows = this.selectedEvents.map((e) => ({
      name: e.recipient_name || '',
      phone: e.recipient_phone || '',
    }));

    this.bulkLeadForm = { status: 'New', leadSpaceId: null };
    this.bulkLeadErrorMessage = '';
    this.isBulkSaving = false;
    this.bulkProgress = { done: 0, total: this.selectedEvents.length };

    // Fetch shared dropdowns (reuse existing lists if already populated)
    const clientId = this.localStorageService.getClientId();
    if (this.leadSpaceListForModal.length === 0) {
      this.leadService.getAllLeadSpace({ client_id: clientId }).subscribe({
        next: (res: any) => {
          this.leadSpaceListForModal = Array.isArray(res) ? res : (res?.data || res?.rows || []);
        },
        error: () => { this.leadSpaceListForModal = []; }
      });
    }
    if (this.leadStatusListForModal.length === 0) {
      this.pipelineService.getAllPipeLine({ client_id: clientId, pipeline_type: 'lead' }).subscribe({
        next: (pipelines: any[]) => {
          const list = Array.isArray(pipelines) ? pipelines : [];
          if (list.length > 0) {
            const stages: any[] = list[0].stages || [];
            this.leadStatusListForModal = stages.map((s: any) => ({ value: s.stage_name, label: s.stage_name }));
            if (this.leadStatusListForModal.length > 0) {
              this.bulkLeadForm.status = this.leadStatusListForModal[0].value;
            }
          }
        },
        error: () => { this.leadStatusListForModal = []; }
      });
    } else if (this.leadStatusListForModal.length > 0) {
      this.bulkLeadForm.status = this.leadStatusListForModal[0].value;
    }

    this.showBulkLeadModal = true;
  }

  closeBulkLeadModal(): void {
    if (this.isBulkSaving) return;
    this.showBulkLeadModal = false;
    this.bulkLeadRows = [];
    this.bulkLeadErrorMessage = '';
  }

  removeBulkRow(index: number): void {
    this.selectedEvents = this.selectedEvents.filter((_, i) => i !== index);
    this.bulkLeadRows = this.bulkLeadRows.filter((_, i) => i !== index);
    if (this.bulkLeadRows.length === 0) {
      this.closeBulkLeadModal();
    }
  }

  bulkProgressPct(): number {
    if (!this.bulkProgress.total) return 0;
    return Math.round((this.bulkProgress.done / this.bulkProgress.total) * 100);
  }

  submitBulkLeads(): void {
    if (this.bulkLeadRows.length === 0) return;

    const clientId = this.localStorageService.getClientId();
    if (!clientId) {
      this.bulkLeadErrorMessage = 'Client ID is missing. Please re-login.';
      return;
    }

    this.isBulkSaving = true;
    this.bulkLeadErrorMessage = '';
    this.bulkProgress = { done: 0, total: this.bulkLeadRows.length };

    const now = new Date();
    const lead_date = now.toISOString().split('T')[0];
    const lead_time = now.toTimeString().slice(0, 5);
    const userId = this.localStorageService.getUserId();
    const centerId = this.localStorageService.getCenterId();

    // Create leads sequentially to avoid rate-limiting
    const createNext = (index: number): void => {
      if (index >= this.bulkLeadRows.length) {
        // All done
        this.isBulkSaving = false;
        const total = this.bulkProgress.done;
        this.toastr.success(`${total} lead(s) created successfully!`, 'Bulk Create Complete');
        this.clearSelection();
        this.closeBulkLeadModal();
        return;
      }

      const row = this.bulkLeadRows[index];
      const event = this.selectedEvents[index];
      const cleanPhone = (row.phone || '').trim();
      const contactName = (row.name || '').trim() || `Contact ${cleanPhone}`;

      const payload: any = {
        client_id: clientId,
        lead_name: contactName,
        lead_phone: cleanPhone,
        lead_email: null,
        lead_source: this.campaignName
          ? `WhatsApp Campaign - ${this.campaignName}`
          : 'WhatsApp Campaign',
        lead_status: this.bulkLeadForm.status || 'New',
        lead_type: 'New Lead',
        lead_date,
        lead_time,
        isEdit: false,
        fields: { phone: cleanPhone, name: contactName },
      };

      if (userId) { payload.created_by = userId; payload.user_id = userId; }
      if (centerId) { payload.center_id = centerId; }
      if (this.bulkLeadForm.leadSpaceId) { payload.lead_space_id = this.bulkLeadForm.leadSpaceId; }

      this.leadService.addLeadData(payload).subscribe({
        next: (res: any) => {
          const newLeadId = res?.lead_id || res?.id || res?.data?.lead_id || res?.data?.id || null;
          // Update row in trackingEvents
          this.trackingEvents.forEach((evt) => {
            if (evt.recipient_phone === cleanPhone) {
              evt.lead_id = newLeadId;
              evt.recipient_name = contactName;
            }
          });
          this.bulkProgress.done++;
          createNext(index + 1);
        },
        error: (err: any) => {
          const msg = err?.error?.message || err?.message || `Failed for ${cleanPhone}`;
          console.error('Bulk lead create error:', msg, err);
          this.bulkProgress.done++;
          // Continue with next even on error
          createNext(index + 1);
        },
      });
    };

    createNext(0);
  }
}
